import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { BehaviorSubject, tap } from 'rxjs'
import { ProductModel } from '../../../core/models/product.model'
import { environment } from '../../../../environments/environment'
import { PaginatedRequestModel } from '../../../core/models/paginated-request.model'
import { PaginatedResponseModel } from '../../../core/models/paginated-response.model'
import { AffairProductModel } from '../../../core/models/affair-product.model'

interface GetProductRequestModel extends PaginatedRequestModel {
  erpSource: String
}

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  private http = inject(HttpClient)
  private planifyApiUrl = environment.planifyApiUrl

  private total = new BehaviorSubject<number>(0)
  private page = new BehaviorSubject<number>(1)
  private loading = new BehaviorSubject<boolean>(false)
  private size = new BehaviorSubject<number>(10)
  private search = new BehaviorSubject<string>('')

  public total$ = this.total.asObservable()
  public page$ = this.page.asObservable()
  public loading$ = this.loading.asObservable()

  private products = new BehaviorSubject<ProductModel[]>([])
  public products$ = this.products.asObservable()

  private productsToPlanify = new BehaviorSubject<ProductModel[]>([])
  public productsToPlanify$ = this.productsToPlanify.asObservable()

  public getProducts() {
    return this.http.get<ProductModel[]>(`${this.planifyApiUrl}/products`).pipe(
      tap((products) => {
        this.products.next(products)
      })
    )
  }

  public getProductToPlanify(params: GetProductRequestModel) {
    this.loading.next(true)

    // Only pass the erpSource to the API since we're doing client-side filtering now
    return this.http
      .post<any>(
        `${this.planifyApiUrl}/affair-products/get-for-programmation-list`,
        {
          erpSource: params.erpSource,
        }
      )
      .pipe(
        tap((res) => {
          // We don't need to set pagination-related values anymore
          // Just set loading to false when the request completes
          this.loading.next(false)
          return res // Return the raw response for component to handle
        })
      )
  }

  public getProductsByMlRef(mlRef: String) {
    return this.http.get<any[]>(
      `${this.planifyApiUrl}/affairs/${mlRef}/affair-products`
    )
  }

  public getSchedulableProductsByMlRef(mlRef: String, reschedule?: boolean) {
    if (reschedule) {
      return this.http.get<AffairProductModel[]>(
        `${this.planifyApiUrl}/affairs/${mlRef}/affair-products/schedulable?reschedule=true`
      )
    }
    return this.http.get<AffairProductModel[]>(
      `${this.planifyApiUrl}/affairs/${mlRef}/affair-products/schedulable`
    )
  }
}
